// ============================================================
// HOOKED — RHYTHM REEL  (Phase 4 — the fight)
// The fish pulls in TEMPO. Beat markers ride the line toward
// the hit mark; tap Space ON the beat to gain ground. Miss and
// the line takes tension. The fish changes meter mid-fight —
// THE LULL becomes THE PANIC — and you must change with it.
//
// v2 overhaul: LOGIC UNCHANGED. The lane now has depth and a
// drifting current; markers are glowing per-Ilk orbs with motion
// trails; the hit mark is a lantern gate; REEL/LINE read as
// filling vials; judgment flashes punch; the panel wears The
// Treatment. Every ref, timing, and resolution is byte-identical
// to v1.
//
// EMPIRE PHYSICS:
//   progress  — fills toward fish.fight (the fish's anima)
//   tension   — dims toward angler.lyf (the LINE — resets every
//               cast, the Lyf Cycle: it breaks in one fight or
//               never)
//   lyt       — the angler's HOOK: ground gained per clean beat
//   fish.lyt  — the fish's edge: tension dealt per missed beat
//
// SPENDS (the Anima Doctrine, live in the fight):
//   [1] SURGE (5 Grit)  — next two beats land perfect, unbidden
//   [2] SLACK (3 Grit)  — all tension releases
//
// THE INVERSION (Prophecy fish only):
//   Survive every movement without snapping, and even if the
//   reel never fills — the fish achieves its ambition. You win.
// ============================================================

import { useEffect, useRef, useState, useCallback } from "react";
import { GM, SD, WHARF_SLAB, facet, bevelPanel, textGlow, ilkAura } from "./theme";
import { SPENDS } from "./angler";
import { FishArt } from "./FishArt";

const LOOKAHEAD_MS = 2000;   // marker travel time across the lane
const PERFECT_MS   = 95;
const GOOD_MS      = 185;
const LATE_KILL_MS = 200;    // beat unresolved this long past due = miss

export function RhythmReel({
  fish,
  angler,                    // { lyt, lyf, anima }
  strikeGrade = "GOOD",      // head start from the strike
  windowMult = 1,            // murk marks widen timing windows
  gaugeReveal = 0,           // ACB: segments revealed in advance
  onSpendAnima,              // (cost) => boolean — parent authorizes the delta
  onResolve,                 // ({ outcome, accuracy, prophecy })
}) {
  // ── Build the full beat schedule once ──────────────────────
  const scheduleRef = useRef(null);
  if (!scheduleRef.current) {
    const beats = [];
    let t = 1600; // lead-in
    fish.pattern.forEach((seg, si) => {
      const gap = 60000 / seg.bpm;
      for (let b = 0; b < seg.beats; b++) {
        beats.push({ time: t, seg: si, state: "pending" }); // pending|hit|perfect|missed
        t += gap;
      }
    });
    scheduleRef.current = { beats, endTime: t + 400 };
  }

  const t0Ref       = useRef(null);
  const rafRef      = useRef(null);
  const doneRef     = useRef(false);
  const progressRef = useRef(strikeGrade === "PERFECT" ? fish.fight * 0.2 : fish.fight * 0.08);
  const tensionRef  = useRef(0);
  const surgeRef    = useRef(0);   // auto-perfect charges remaining
  const statsRef    = useRef({ perfect: 0, good: 0, miss: 0, total: scheduleRef.current.beats.length });

  const [, force] = useState(0);
  const [now, setNow] = useState(0);
  const [banner, setBanner] = useState({ text: fish.pattern[0].label, bpm: fish.pattern[0].bpm, key: 0 });
  const [flash, setFlash] = useState(null); // { grade, key }
  const lastSegRef = useRef(0);

  const finish = useCallback((outcome) => {
    if (doneRef.current) return;
    doneRef.current = true;
    cancelAnimationFrame(rafRef.current);
    const s = statsRef.current;
    const judged = s.perfect + s.good + s.miss;
    const accuracy = judged ? (s.perfect + s.good * 0.6) / judged : 0;
    const prophecy = outcome === "EXHAUSTED" && fish.inversion;
    onResolve?.({ outcome: prophecy ? "PROPHECY" : outcome, accuracy, prophecy });
  }, [fish, onResolve]);

  // ── Main loop ───────────────────────────────────────────────
  useEffect(() => {
    t0Ref.current = performance.now();
    const tick = (nowAbs) => {
      if (doneRef.current) return;
      const t = nowAbs - t0Ref.current;
      setNow(t);

      const { beats, endTime } = scheduleRef.current;

      // kill overdue beats
      for (const b of beats) {
        if (b.state === "pending" && t > b.time + LATE_KILL_MS) {
          if (surgeRef.current > 0) {
            surgeRef.current -= 1;
            b.state = "perfect";
            statsRef.current.perfect += 1;
            progressRef.current += angler.lyt * 1.5;
            setFlash({ grade: "SURGE", key: Math.random() });
          } else {
            b.state = "missed";
            statsRef.current.miss += 1;
            tensionRef.current += fish.lyt;
            setFlash({ grade: "MISS", key: Math.random() });
          }
          force(x => x + 1);
        }
      }

      // segment banner
      const nextPending = beats.find(b => b.state === "pending");
      if (nextPending && nextPending.seg !== lastSegRef.current) {
        lastSegRef.current = nextPending.seg;
        const seg = fish.pattern[nextPending.seg];
        setBanner({ text: seg.label, bpm: seg.bpm, key: nextPending.seg });
      }

      // resolutions
      if (tensionRef.current >= angler.lyf) { finish("SNAPPED"); return; }
      if (progressRef.current >= fish.fight) { finish("LANDED"); return; }
      if (t >= endTime) {
        finish(progressRef.current >= fish.fight ? "LANDED" : "EXHAUSTED");
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [angler.lyt, angler.lyf, fish, finish]);

  // ── Input ───────────────────────────────────────────────────
  useEffect(() => {
    const tap = () => {
      if (doneRef.current) return;
      const t = performance.now() - t0Ref.current;
      const { beats } = scheduleRef.current;
      // nearest pending beat
      let best = null, bestD = Infinity;
      for (const b of beats) {
        if (b.state !== "pending") continue;
        const d = Math.abs(t - b.time);
        if (d < bestD) { bestD = d; best = b; }
      }
      if (!best || bestD > GOOD_MS * windowMult + 160) {
        // stray tap — the fish feels your fumbling
        tensionRef.current += fish.lyt * 0.4;
        setFlash({ grade: "STRAY", key: Math.random() });
        force(x => x + 1);
        return;
      }
      if (surgeRef.current > 0 || bestD <= PERFECT_MS * windowMult) {
        if (surgeRef.current > 0 && bestD > PERFECT_MS * windowMult) surgeRef.current -= 1;
        best.state = "perfect";
        statsRef.current.perfect += 1;
        progressRef.current += angler.lyt * 1.5;
        setFlash({ grade: "PERFECT", key: Math.random() });
      } else if (bestD <= GOOD_MS * windowMult) {
        best.state = "hit";
        statsRef.current.good += 1;
        progressRef.current += angler.lyt;
        setFlash({ grade: "GOOD", key: Math.random() });
      } else {
        best.state = "missed";
        statsRef.current.miss += 1;
        tensionRef.current += fish.lyt;
        setFlash({ grade: "MISS", key: Math.random() });
      }
      force(x => x + 1);
    };

    const onKey = (e) => {
      if (e.code === "Space") { e.preventDefault(); tap(); }
      if (e.key === "1") { e.preventDefault(); trySurge(); }
      if (e.key === "2") { e.preventDefault(); trySlack(); }
    };
    const trySurge = () => {
      if (doneRef.current) return;
      if (onSpendAnima?.(SPENDS.SURGE.cost)) {
        surgeRef.current += 2;
        setFlash({ grade: "SURGE READY", key: Math.random() });
      }
    };
    const trySlack = () => {
      if (doneRef.current) return;
      if (onSpendAnima?.(SPENDS.SLACK.cost)) {
        tensionRef.current = 0;
        setFlash({ grade: "SLACK", key: Math.random() });
        force(x => x + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    window.__hookedReelTap = tap;          // mobile tap bridge
    window.__hookedSurge = trySurge;
    window.__hookedSlack = trySlack;
    return () => {
      window.removeEventListener("keydown", onKey);
      delete window.__hookedReelTap;
      delete window.__hookedSurge;
      delete window.__hookedSlack;
    };
  }, [angler.lyt, fish, windowMult, onSpendAnima]);

  // ── Render ──────────────────────────────────────────────────
  const { beats } = scheduleRef.current;
  const visible = beats.filter(b => b.state === "pending" && b.time - now < LOOKAHEAD_MS && b.time - now > -LATE_KILL_MS);
  const progPct = Math.min(100, (progressRef.current / fish.fight) * 100);
  const tensPct = Math.min(100, (tensionRef.current / angler.lyf) * 100);
  const tenseHot = tensPct > 65;
  const aura = ilkAura(fish.ilk);

  const flashColor = {
    PERFECT: GM.goldBright, GOOD: SD.tealLight, MISS: SD.redBright,
    STRAY: SD.redDim, SURGE: SD.purpleLight, "SURGE READY": SD.purpleLight, SLACK: SD.foam,
  }[flash?.grade] || GM.ink;

  return (
    <div style={{
      position: "fixed", left: "50%", bottom: 40, transform: "translateX(-50%)",
      width: "min(560px, 94vw)", zIndex: 320,
      background: WHARF_SLAB, clipPath: facet(14),
      padding: "16px 18px 14px",
      ...bevelPanel(aura),
      fontFamily: GM.fontDisplay, userSelect: "none",
      animation: "fadeUp 0.25s ease",
    }}>
      {/* movement banner */}
      <div key={banner.key} style={{
        textAlign: "center", fontSize: 14, letterSpacing: 4,
        color: GM.goldBright, ...textGlow(GM.gold),
        animation: "beatPop 0.5s ease",
      }}>
        {banner.text} <span style={{ color: aura, fontSize: 10, letterSpacing: 1 }}>· {banner.bpm} BPM</span>
      </div>

      {/* ACB reveal */}
      {gaugeReveal > 0 && (
        <div style={{ textAlign: "center", fontSize: 9, color: SD.tealLight, letterSpacing: 1.5, marginTop: 3, ...textGlow(SD.teal) }}>
          THE GAUGE SPOKE: {fish.pattern.slice(0, gaugeReveal).map(s => `${s.label} ${s.bpm}`).join(" → ")}
        </div>
      )}

      {/* THE FIGHT — rod under load ↔ taut line ↔ the fish pulling.
          This is the "there's something alive on the end" layer.
          A miss (or high tension) makes the rod YANK and the fish
          HEADSHAKE. Purely visual — resolution logic is untouched. */}
      <FightScene fish={fish} aura={aura} tensPct={tensPct}
        yankKey={flash && (flash.grade === "MISS" || flash.grade === "STRAY") ? flash.key : null} />

      {/* the lane — water with a drifting current */}
      <div style={{
        position: "relative", height: 68, marginTop: 12,
        background: `linear-gradient(180deg, ${SD.waterDeep} 0%, ${SD.void1} 100%)`,
        borderRadius: 9, overflow: "hidden",
        boxShadow: `inset 0 2px 6px rgba(0,0,0,0.7), inset 0 0 0 1px ${GM.inkBrown}`,
      }}>
        {/* current streaks */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.14,
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0 30px, ${SD.caustic} 30px 31px)`,
          animation: "foilDrift 3s linear infinite",
        }} />
        {/* hit-mark lantern gate */}
        <div style={{
          position: "absolute", left: "12%", top: 5, bottom: 5, width: 3,
          background: `linear-gradient(180deg, ${GM.goldPale}, ${GM.gold})`, borderRadius: 2,
          boxShadow: `0 0 14px ${GM.gold}`,
        }} />
        <div style={{
          position: "absolute", left: "12%", top: "50%",
          width: 38, height: 38, transform: "translate(-50%,-50%)",
          borderRadius: "50%", border: `2px solid ${GM.gold}88`,
          boxShadow: `0 0 12px ${GM.gold}44, inset 0 0 8px ${GM.gold}33`,
          background: `radial-gradient(circle, ${GM.gold}12, transparent 70%)`,
        }} />

        {/* markers ride right → left, glowing per-Ilk orbs with trails */}
        {visible.map((b, i) => {
          const x = 12 + ((b.time - now) / LOOKAHEAD_MS) * 84; // % across lane
          const closeness = Math.max(0, 1 - Math.abs(x - 12) / 30);
          return (
            <div key={`${b.time}-${i}`}>
              <div style={{
                position: "absolute", left: `${x + 5}%`, top: "50%",
                width: 26, height: 6, transform: "translate(-50%,-50%)",
                background: `linear-gradient(90deg, transparent, ${fish.color}66)`,
                borderRadius: 3, filter: "blur(1px)",
              }} />
              <div style={{
                position: "absolute", left: `${x}%`, top: "50%",
                width: 24, height: 24, transform: "translate(-50%,-50%)",
                borderRadius: "50%",
                background: `radial-gradient(circle at 35% 30%, #fff, ${fish.color} 45%, ${SD.waterDeep})`,
                border: `2px solid ${aura}`,
                boxShadow: `0 0 ${8 + closeness * 16}px ${fish.color}, inset 0 0 6px #fff8`,
              }} />
            </div>
          );
        })}

        {/* judgment flash */}
        {flash && (
          <div key={flash.key} style={{
            position: "absolute", left: "12%", top: -4, transform: "translateX(-50%)",
            fontSize: 12, fontWeight: 900, letterSpacing: 2,
            color: flashColor, textShadow: `0 0 10px ${flashColor}`,
            animation: "beatPop 0.4s ease", whiteSpace: "nowrap", zIndex: 3,
          }}>
            {flash.grade}
          </div>
        )}
      </div>

      {/* REEL progress + LINE tension */}
      <div style={{ marginTop: 12 }}>
        <Bar label="REEL" pct={progPct} color={GM.goldBright} bg={GM.goldDim} icon="◆" />
        <div style={{ height: 7 }} />
        <Bar label="LINE" pct={tensPct} color={SD.redBright} bg={SD.redDim} icon="✕"
             style={tenseHot ? { animation: "tensionPulse 0.5s ease-in-out infinite" } : {}} />
      </div>

      {/* spends */}
      <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "center" }}>
        <SpendBtn n="1" label={SPENDS.SURGE.label} cost={SPENDS.SURGE.cost}
          disabled={angler.anima < SPENDS.SURGE.cost}
          onClick={() => window.__hookedSurge?.()} />
        <SpendBtn n="2" label={SPENDS.SLACK.label} cost={SPENDS.SLACK.cost}
          disabled={angler.anima < SPENDS.SLACK.cost}
          onClick={() => window.__hookedSlack?.()} />
        <div
          onPointerDown={(e) => { e.preventDefault(); window.__hookedReelTap?.(); }}
          style={{
            padding: "9px 26px", borderRadius: 9, cursor: "pointer",
            background: `linear-gradient(180deg, ${GM.goldPale}, ${GM.gold} 45%, ${GM.goldDeep})`,
            color: GM.pageDeep, fontWeight: 900, fontSize: 12, letterSpacing: 2,
            fontFamily: GM.fontUI,
            boxShadow: `0 3px 0 ${GM.goldDim}, 0 6px 14px rgba(0,0,0,0.5), inset 0 1px 0 #fff8`,
          }}>
          REEL
        </div>
      </div>
    </div>
  );
}

function FightScene({ fish, aura, tensPct, yankKey }) {
  const [yank, setYank] = useState(0);
  const lastKey = useRef(null);
  useEffect(() => {
    if (yankKey && yankKey !== lastKey.current) {
      lastKey.current = yankKey;
      setYank(y => y + 1);
      const t = setTimeout(() => {}, 300);
      return () => clearTimeout(t);
    }
  }, [yankKey]);

  // load rises with tension: the rod bows harder, the fish fights harder
  const load = tensPct / 100;
  const rodBend = 3 + load * 9;           // degrees the rod tip bows
  const taut = 0.5 + load * 0.5;          // line opacity/tautness
  const shakeSpeed = Math.max(0.5, 1.4 - load);  // faster headshakes under load

  return (
    <div style={{
      position: "relative", height: 56, marginTop: 10, marginBottom: 2,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 6px", overflow: "visible",
    }}>
      {/* the rod tip on the left — bends under load, yanks on a miss */}
      <div key={`rod-${yank}`} style={{
        width: 6, height: 44, transformOrigin: "bottom center",
        transform: `rotate(-${rodBend}deg)`,
        animation: yank ? "rodYank 0.3s ease-out" : `rodStrain ${shakeSpeed + 0.4}s ease-in-out infinite`,
        background: `linear-gradient(180deg, ${GM.goldPale}, ${GM.gold} 40%, ${GM.ropeDim})`,
        borderRadius: 3, boxShadow: `0 0 6px ${GM.gold}55`,
        flexShrink: 0,
      }} />

      {/* the taut line arcing from rod to fish — tightens with tension */}
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" style={{
        position: "absolute", left: 12, right: 44, top: "50%",
        transform: "translateY(-50%)", height: 40, width: "calc(100% - 56px)",
      }}>
        <path d={`M 0 8 Q 50 ${8 + (1 - load) * 20} 100 22`}
          fill="none" stroke={aura} strokeWidth="0.6" strokeOpacity={taut}
          vectorEffect="non-scaling-stroke"
          style={{ animation: `lineTaut ${shakeSpeed}s ease-in-out infinite` }} />
      </svg>

      {/* the hooked fish on the right — thrashing, headshaking under load */}
      <div key={`fish-${yank}`} style={{
        position: "relative", flexShrink: 0,
        animation: yank
          ? "fishHeadshake 0.3s ease-in-out"
          : `fishHeadshake ${shakeSpeed * 1.6}s ease-in-out infinite`,
        filter: `drop-shadow(0 0 6px ${aura}66)`,
      }}>
        <FishArt fish={fish} size={44} animate facing={-1} />
      </div>
    </div>
  );
}

function Bar({ label, pct, color, bg, icon, style = {} }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ fontSize: 9, letterSpacing: 2, color: GM.inkDim, width: 40, display: "flex", gap: 3, alignItems: "center" }}>
        <span style={{ color }}>{icon}</span>{label}
      </div>
      <div style={{
        flex: 1, height: 12, borderRadius: 6, overflow: "hidden",
        background: `linear-gradient(180deg, ${GM.pageDeep}, ${GM.page})`,
        boxShadow: `inset 0 1px 3px rgba(0,0,0,0.6), inset 0 0 0 1px ${GM.inkBrown}`,
      }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: `linear-gradient(90deg, ${bg}, ${color})`,
          boxShadow: `0 0 12px ${color}88`,
          transition: "width 0.12s ease", ...style,
        }} />
      </div>
      <div style={{ fontSize: 9, color: GM.inkDim, width: 30, textAlign: "right", fontFamily: GM.fontUI }}>{Math.round(pct)}%</div>
    </div>
  );
}

function SpendBtn({ n, label, cost, disabled, onClick }) {
  return (
    <div
      onPointerDown={(e) => { e.preventDefault(); if (!disabled) onClick?.(); }}
      style={{
        padding: "7px 13px", borderRadius: 9,
        border: `1px solid ${disabled ? GM.inkBrown : SD.purpleLight}88`,
        color: disabled ? GM.inkDim : SD.purpleLight,
        fontSize: 10, letterSpacing: 1.5, cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.45 : 1, fontFamily: GM.fontUI, userSelect: "none",
        background: disabled ? "transparent" : `${SD.purple}18`,
        boxShadow: disabled ? "none" : `0 0 10px ${SD.purple}22, inset 0 1px 0 ${SD.purpleLight}22`,
      }}>
      [{n}] {label} · {cost}✦
    </div>
  );
}
